// Common
import React, { useState, useRef, useCallback } from 'react';

// Components
import components from '../../components';
import DeviceActionButtons from './DeviceActionButtons';
import BulkActionButtons from './bulkActionButtons';
import tableHeaders from './tableHeaders';

// Utilities
import deviceUtils from '../../utils/device';
import API from '../../services/api';
import { useDispatch } from 'react-redux';
import { setAlert } from '../../redux/dashboard/actions';
import utils from '../../utils';
import SetPresetModal from './setPresetModal/setPresetModal';

const { Table } = components;

const DevicesTable = ({
  project,
  searchBy,
  filterBy,
  isItemDetailsClosed,
  isAdminUser,
  lastUpdated,
  isAdvertiserUser,
  onItemSelected,
  onDeviceCountChanged
}) => {
  // Table hooks
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [initialSortColumn] = useState('displayName');

  const [isModalOpen, setIsModalOpen] = useState(false);

  const nextPageToken = useRef('');
  const filters = useRef('');
  const sorting = useRef('displayName, id');
  const devices = useRef([]);
  const oldProjectId = useRef('');
  const oldLastUpdated = useRef(lastUpdated);

  const dispatch = useDispatch();

  const getFilters = () => {
    let newFilters = [];

    if (searchBy !== '') {
      newFilters.push(`(displayName ILIKE "%${searchBy}%" OR id ILIKE "%${searchBy}%")`);
    }

    if (!isAdminUser) {
      newFilters.push('(archived = false)');
    }

    filterBy.forEach(filter => {
      const operator = filter.operator ? filter.operator : '=';
      const value = filter.type === 'version' ? `"${filter.value}"` : filter.value;
      newFilters.push(`(${filter.path} ${operator} ${value})`);
    });

    return newFilters.join(' AND ');
  };

  const getSorting = sortBy => {
    if (sortBy.length > 0 && sortBy[0].id) {
      return `${sortBy[0].id} ${sortBy[0].desc ? 'DESC' : 'ASC'}, id`;
    }

    return (sorting.current = 'displayName, id');
  };

  const getDeviceList = (newDevices, forceUpdate) => {
    // Merge new data with previous data if sorting and filters hasn't change
    if (!forceUpdate) {
      return [...devices.current, ...newDevices];
    }

    // Overwrite list with new data
    return newDevices;
  };

  // This is called when the table needs new data
  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      setLoading(true);
      // Keep old sorting and filters to handle changes
      const oldSorting = sorting.current;
      const oldFilters = filters.current;

      // Apply Sorting and filters
      filters.current = encodeURI(getFilters());
      sorting.current = getSorting(sortBy);

      const forceUpdate =
        oldSorting !== sorting.current ||
        oldFilters !== filters.current ||
        lastUpdated !== oldLastUpdated.current ||
        project.id !== oldProjectId.current;

      // Clear pageToken if project, filter or sorting has changed
      if (forceUpdate) {
        nextPageToken.current = '';
        setLoading(true);
        oldLastUpdated.current = lastUpdated;
      }

      // Fetch new page
      API.getDevices(project.id, nextPageToken.current, filters.current, 15, sorting.current)
        .then(response => {
          // Normalize devices adding extra info
          const newDevices = deviceUtils.addInformation({ devices: response.devices });

          // Update device list
          devices.current = getDeviceList(newDevices, forceUpdate);

          nextPageToken.current = response.nextPageToken;
          oldProjectId.current = project.id;

          // Compute total number of items
          setItemsCount(response.totalSize);

          // Update item count when there is no filters applied
          // if (filters.current === '') {
          onDeviceCountChanged(response.totalSize);
          // }

          // Update table indexes
          const startRow = pageSize * pageIndex;
          const endRow = startRow + pageSize;

          // Slice items from device list
          //HACK: Doing this in two parts won't break the interface with empty table on pressing next page.
          const slicedDevicesData = devices.current.slice(startRow, endRow);
          setData(slicedDevicesData);

          // Compute total page count
          setPageCount(Math.ceil(response.totalSize / pageSize));
          setLoading(false);
        })
        .catch(() => {
          dispatch(
            setAlert(
              utils.generateAlert(
                'You have no permission to see devices on this Site group. Try choosing another one',
                'error'
              )
            )
          );
          setData([]);
          setLoading(false);
        });
    },
    [searchBy, filterBy, initialSortColumn, isAdminUser, project, lastUpdated]
  );

  const renderActionButtons = device => (
    <DeviceActionButtons device={device} isAdminUser={isAdminUser} isAdvertiserUser={isAdvertiserUser} />
  );

  const renderBulkActionButtons = () => (
    <BulkActionButtons
      devices={selectedDevices}
      isAdvertiserUser={isAdvertiserUser}
      onCommand={setIsModalOpen}
      isAdmin={isAdminUser}
    />
  );

  return (
    <>
      <Table
        columns={tableHeaders}
        data={data}
        dataLabel={'devices'}
        fetchData={fetchData}
        loading={loading}
        searchBy={searchBy}
        filterBy={filterBy}
        pageCount={pageCount}
        itemsCount={itemsCount}
        resetOnChange={project} // reset table when project change
        hasInteractiveRows
        initialSortColumn={initialSortColumn.id}
        isItemDetailsClosed={isItemDetailsClosed}
        onItemSelected={onItemSelected}
        onDataSelected={setSelectedDevices}
        bulkActionButtons={renderBulkActionButtons}
        actionButtons={device => renderActionButtons(device)}
      />
      <SetPresetModal isOpen={isModalOpen} devices={selectedDevices} toggleModal={() => setIsModalOpen(!isModalOpen)} />
    </>
  );
};

export default DevicesTable;
